// ============================================
// checkout.js — Flujo de checkout con Medusa v2 + Stripe
// dirección → método de envío → sesión de pago → completar orden
// ============================================
import { MEDUSA_REGION_ID, STRIPE_PROVIDER_ID } from './config';
import { storeFetch } from './storeFetch';

// Carrito/checkout autenticados si hay sesión (ver storeFetch.js).
const medusaRequest = storeFetch;

// País (etiqueta del formulario) → country_code ISO-2 en minúsculas que espera Medusa.
const COUNTRY_CODE = {
  'México': 'mx',
  'Mexico': 'mx',
  'Estados Unidos': 'us',
  'España': 'es',
  'Espana': 'es',
  'Argentina': 'ar',
  'Colombia': 'co',
};

export function toMedusaAddress(form) {
  return {
    first_name: form.firstName,
    last_name: form.lastName,
    address_1: form.address,
    address_2: form.colonia || '',
    city: form.city,
    province: form.state,
    postal_code: form.zip,
    country_code: COUNTRY_CODE[form.country] || 'mx',
    phone: form.phone || '',
  };
}

// Guarda email + direcciones en el carrito. Reutiliza la misma dirección para
// envío y facturación. Fija la región si está configurada por env.
export async function setCartDetails(cartId, form) {
  const address = toMedusaAddress(form);
  const payload = {
    email: form.email,
    shipping_address: address,
    billing_address: address,
  };
  if (MEDUSA_REGION_ID) payload.region_id = MEDUSA_REGION_ID;

  const { cart } = await medusaRequest(`/store/carts/${cartId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return cart;
}

// Opciones de envío disponibles para el carrito (con precio ya calculado).
export async function listShippingOptions(cartId) {
  const data = await medusaRequest(`/store/shipping-options?cart_id=${cartId}`);
  return data.shipping_options || [];
}

// Asigna un método de envío al carrito. `data` solo lo usa "Envío por peso"
// (monto cotizado + firma, ver shippingQuote.js).
export async function addShippingMethod(cartId, optionId, data) {
  const { cart } = await medusaRequest(`/store/carts/${cartId}/shipping-methods`, {
    method: 'POST',
    body: JSON.stringify(data ? { option_id: optionId, data } : { option_id: optionId }),
  });
  return cart;
}

// Trae el carrito con su payment_collection (para reutilizarla si ya existe).
async function retrieveCartWithCollection(cartId) {
  const data = await medusaRequest(
    `/store/carts/${cartId}?fields=*payment_collection,*payment_collection.payment_sessions`
  );
  return data.cart;
}

// Inicia (o reutiliza) la sesión de pago de Stripe y devuelve el client_secret.
// Replica lo que hace el SDK: crea la payment collection del carrito si no
// existe, y luego crea la sesión con el provider pp_stripe_stripe.
export async function initStripePaymentSession(cartId) {
  const cart = await retrieveCartWithCollection(cartId);

  let collectionId = cart?.payment_collection?.id;
  if (!collectionId) {
    const { payment_collection } = await medusaRequest('/store/payment-collections', {
      method: 'POST',
      body: JSON.stringify({ cart_id: cartId }),
    });
    collectionId = payment_collection.id;
  }

  const { payment_collection } = await medusaRequest(
    `/store/payment-collections/${collectionId}/payment-sessions`,
    {
      method: 'POST',
      body: JSON.stringify({ provider_id: STRIPE_PROVIDER_ID }),
    }
  );

  const session = (payment_collection.payment_sessions || []).find(
    (s) => s.provider_id === STRIPE_PROVIDER_ID
  );
  const clientSecret = session?.data?.client_secret;
  if (!clientSecret) {
    throw new Error('No se recibió client_secret de Stripe. ¿Está Stripe habilitado en la región?');
  }
  return { clientSecret, paymentCollectionId: collectionId };
}

// Completa el carrito → crea la orden. Devuelve { type, order } en éxito
// o { type: 'cart', error } si algo falló al autorizar/completar.
export async function completeCart(cartId) {
  return medusaRequest(`/store/carts/${cartId}/complete`, { method: 'POST' });
}

export async function retrieveCart(cartId) {
  const data = await medusaRequest(`/store/carts/${cartId}`);
  return data.cart;
}
